import { StatusModal } from '@/components/StatusModal';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useState } from 'react';

type Zakazka = {
  id: string;
  cislo: string;
  produkt_kod: string;
  produkt: string;
  foto: string;
  velikosti: { label: string; qty: number }[];
  celkem: number;
  stav: string;
  cas_siti_h: number | null;
  /** Kdy potřebujeme balík zpět — 'YYYY-MM-DD', nebo null. */
  termin: string | null;
};

const DNY = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'];

/** '2026-10-05' → „Pondělí 5. 10." */
function fmtTermin(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const den = new Date(y, m - 1, d);
  return `${DNY[den.getDay()]} ${d}. ${m}.`;
}

/** 2.98 h → „2 h 59 min". */
function fmtCas(h: number | null): string {
  if (!h || h <= 0) return '—';
  const min = Math.round(h * 60);
  return `${Math.floor(min / 60)} h ${min % 60} min`;
}

/**
 * Externí dílny → Věra Martínková. Rozpracované zakázky švadlenky „Mamka"
 * jen ke čtení — karty jako v Plánu stříhání: fotka, kód, název, čas šití
 * a velikosti s kusy. Nic dalšího.
 */
export function ExterniVeraPage() {
  const [zakazky, setZakazky] = useState<Zakazka[] | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch<{ zakazky: Zakazka[] }>('/api/vyroba/externi/vera');
      setZakazky(d.zakazky);
    } catch (e: unknown) {
      setZakazky([]);
      setToast(e instanceof ApiError ? e.describe() : 'Načtení zakázek selhalo');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const kusu = (zakazky ?? []).reduce((n, z) => n + z.celkem, 0);
  const hodin = (zakazky ?? []).reduce((n, z) => n + (z.cas_siti_h ?? 0), 0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Věra Martínková</h1>
        {zakazky && zakazky.length > 0 && (
          <p className="text-base text-text-secondary mt-1">
            {zakazky.length} zakázek · celkem <strong className="text-text">{kusu} ks</strong> · čas
            šití <strong className="text-text">{fmtCas(hodin)}</strong>
          </p>
        )}
      </div>

      {zakazky === null ? (
        <p className="text-sm text-text-muted">Načítám…</p>
      ) : zakazky.length === 0 ? (
        <div className="bg-surface rounded-xl shadow-card p-10 text-center text-text-muted">
          Žádná rozpracovaná zakázka.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {zakazky.map((z) => (
            <div key={z.id} className="bg-surface rounded-xl shadow-card p-5 flex gap-5">
              {z.foto ? (
                <img
                  src={z.foto}
                  alt=""
                  loading="lazy"
                  className="h-[150px] w-[122px] sm:h-[177px] sm:w-[150px] shrink-0 rounded-lg object-contain border border-border bg-white p-3"
                />
              ) : (
                <div className="h-[150px] w-[122px] sm:h-[177px] sm:w-[150px] shrink-0 rounded-lg border border-border bg-bg" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-text-muted font-mono">{z.produkt_kod}</div>
                <div className="text-lg font-bold text-text leading-snug">{z.produkt}</div>
                <div className="text-sm text-text mt-1">
                  <span className="font-semibold">Čas šití:</span> {fmtCas(z.cas_siti_h)}
                </div>
                {z.termin && (
                  <div className="mt-2 inline-block rounded-lg border border-warning/40 bg-warning-light px-3 py-1.5">
                    <span className="text-xs font-semibold text-warning uppercase tracking-wide block">
                      Potřebujeme zpět
                    </span>
                    <span className="text-lg font-bold text-text">{fmtTermin(z.termin)}</span>
                  </div>
                )}
                <div className="mt-3 divide-y divide-border-light border-t border-border-light">
                  {z.velikosti.map((v) => (
                    <div key={v.label} className="flex justify-between py-2 text-base">
                      <span className="text-text">{v.label || '—'}</span>
                      <span className="font-bold text-text">{v.qty} ks</span>
                    </div>
                  ))}
                </div>
                <div className="text-sm text-text-muted pt-2 border-t border-border-light">
                  Celkem {z.celkem} ks
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {toast && <StatusModal message={toast} kind="error" onClose={() => setToast(null)} />}
    </div>
  );
}
